// src/views/kiddo-games/recognize/KiddoRecognizeView.tsx
// Rozpoznaj słowo Easy, w menu „Przeczytaj i powiedz co to znaczy” — kopia
// src/views/recognize/RecognizeView.tsx, rozwijana osobno.
// Różnice: własne style, następna gra z grupy Easy, osobne zapamiętanie przełącznika polskiego audio, a karta „?”
// pokazuje obrazek słowa zamiast polskiego napisu.
/* eslint-disable max-len */
import React, {
  FC, memo, useState, useEffect, useRef, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import ReactCardFlip from 'react-card-flip';
import { shuffle } from 'lodash';
import classes from './KiddoRecognizeView.module.less';
import FixWidthContent from '../../../components/FixWidthContent';
import withTagManager from '../../../hooks/withTagManager';
import { WordFromList } from './WordFromList';
// Kopie ikon z recognize/ dla Easy — obwódka z kreski, żeby styl mógł ją pocienić jak na ekranie końcowym
import { ReactComponent as Repeat } from '../../../assets/icons/games/kiddo/refresh-ccw-rec.svg';
import { ReactComponent as Correct } from '../../../assets/icons/games/kiddo/check-circle-2.svg';
import InfoButton from '../../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../../auth/slice/selectors';
import EndGameInfo from '../../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../../hooks/useGoToRoute';
import { Path } from '../../../constants/Path';
import { RightSideButtons } from '../../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../../components/EditTermModal/EditTermButton';
import { usePolishAudio } from '../../../hooks/usePolishAudio';
import useAudioPlayer from '../../../hooks/useAudioPlayer';
import { PolishAudioButton } from '../../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../../constants/Roles';
import WordCounter from '../../../components/WordCounter';
import { EditListButton } from '../../../components/EditListButton/EditListButton';
import { BASE_API_URL } from '../../../services/ApiService';
import { cleanTextForTTS } from '../../../utils/textBracketSanitize';
import { getListComposition, getListWordId, withListTexts } from '../../../utils/gameWords';
import useActivityTracking from '../../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../../services/activity';

export const KiddoRecognizeView: FC = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const { words } = activeList || { words: [] };

  const [currentWords, setCurrentWords] = useState(() => shuffle(words));
  const [wordOnScreen, setWordOnScreen] = useState(0);
  const [listEndInfo, setListEndInfo] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.RECOGNIZE_EASY);

  const { play: playAudio, stop: stopAudio } = useAudioPlayer();

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
    stopPolishAudio,
  } = usePolishAudio({ storageKey: 'kiddo_recognize_playPolish' });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToNext = useGoToRoute(Path.KIDDO_RECOGNIZE2);

  const repeatGame = useCallback(() => {
    stopAudio();
    stopPolishAudio();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCurrentWords(shuffle(words || []));
    setListEndInfo(false);
    setWordOnScreen(0);
    setIsFlipped(false);
    setIsTransitioning(false);
  }, [stopAudio, stopPolishAudio, words]);

  // Gra zaczyna od nowa przy innym składzie listy (inna lista, dodane albo usunięte słowa). Poprawka tekstu,
  // np. z „Edytuj zwrot”, gry nie przerywa: słowa w grze dostają nowy tekst, a gra zostaje na tym samym słowie
  const listCompositionRef = useRef('');

  useEffect(() => {
    const listComposition = getListComposition(words);

    if (listComposition === listCompositionRef.current) {
      setCurrentWords((prev) => withListTexts(prev, words));

      return;
    }

    listCompositionRef.current = listComposition;
    setCurrentWords(shuffle(words));
    setWordOnScreen(0);
    setIsFlipped(false);
    setListEndInfo(false);
  }, [words]);

  // Karta „?”: po kliknięciu pokazuje obrazek słowa, a lektor czyta angielskie słowo i po nim polskie tłumaczenie
  // (polskie tylko przy włączonym PL). Kliknięcie obrazka obraca kartę z powrotem, bez dźwięku
  const handleFlip = useCallback((withSound: boolean = false) => {
    if (isTransitioning) return;

    setIsFlipped((current) => !current);

    const current = currentWords[wordOnScreen];
    if (!withSound || !current) return;

    // Ponowne kliknięcie „?” zaczyna czytanie od nowa — polskie z poprzedniego nie może wejść na angielskie
    stopPolishAudio();

    const cleanTerm = cleanTextForTTS(current.term);
    if (!cleanTerm) {
      playPolishAudio(current.definition);

      return;
    }

    playAudio(`${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`, () => {
      playPolishAudio(current.definition);
    }).catch(() => {});
  }, [currentWords, wordOnScreen, isTransitioning, playAudio, playPolishAudio, stopPolishAudio]);

  const handleNextClick = useCallback((): void => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // CORRECT to samoocena ucznia — gra nie zna wyniku. Tak samo REPEAT niżej; „?” (podgląd obrazka) to nie próba
    recordAnswer(null, currentWords[wordOnScreen]?.term);

    stopAudio();
    stopPolishAudio();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (wordOnScreen < currentWords.length - 1) {
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
        setIsTransitioning(false);
      }, 100);
    } else {
      setListEndInfo(true);
      setIsTransitioning(false);
    }
  }, [isTransitioning, stopAudio, stopPolishAudio, wordOnScreen, currentWords]);

  const handleRepeat = useCallback((): void => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    const word = currentWords[wordOnScreen];
    if (!word) {
      setIsTransitioning(false);

      return;
    }

    recordAnswer(null, word.term);

    const wordToRepeat = {
      ...word,
      id: `${word.id}_repeat_${Date.now()}`,
      listWordId: getListWordId(word),
    };

    stopAudio();
    stopPolishAudio();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (wordOnScreen === currentWords.length - 1) {
      // Przed powtórką wchodzi inne słowo — pierwsze z listy, ale nie to z błędem (mogło stać na początku
      // i wrócić na koniec z wcześniejszego REPEAT). Gdy innego nie ma (wszystkie słowa zapisane tak samo), słowo wraca od razu
      const filler = currentWords.find(({ term }) => term !== wordToRepeat.term);
      const updatedCurrentWords = filler ? [...currentWords, filler, wordToRepeat] : [...currentWords, wordToRepeat];
      setCurrentWords(updatedCurrentWords);
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
        setIsTransitioning(false);
      }, 100);
    } else {
      setCurrentWords((prev) => [...prev, wordToRepeat]);
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
        setIsTransitioning(false);
      }, 100);
    }
  }, [isTransitioning, currentWords, wordOnScreen, stopAudio, stopPolishAudio]);

  useEffect(() => () => {
    stopPolishAudio();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [stopPolishAudio]);

  if (!currentWords || currentWords.length === 0) {
    return (
      <FixWidthContent>
        <div>{t('games:states.noWords')}</div>
      </FixWidthContent>
    );
  }

  const current = currentWords[wordOnScreen];

  return (
    <>
      <FixWidthContent className={classes.main}>
        {!listEndInfo && (
        <WordCounter
          current={wordOnScreen + 1}
          total={currentWords.length}
        />
        )}
        {/* Na telefonie trzymanym poziomo karta ze słowem i karta „?” stoją obok siebie (KiddoRecognizeView.module.less) */}
        {!listEndInfo && current && (
          <div className={classes.stage}>
            <div>
              <WordFromList
                id={current.id}
                content={current.term}
              />
            </div>

            <div className={classes.flipper}>
              <ReactCardFlip
                isFlipped={isFlipped}
                flipDirection="vertical"
                flipSpeedFrontToBack={0.15}
                flipSpeedBackToFront={0.15}
              >
                <div
                  className={classes.front}
                  onClick={() => handleFlip(true)}
                  aria-hidden="true"
                >
                  ?
                </div>
                <div
                  className={classes.back}
                  onClick={() => handleFlip(false)}
                  aria-hidden="true"
                >
                  {/* Słowo bez obrazka pokazuje, jak dawniej, polski napis */}
                  {current.image ? <img src={current.image} alt={current.definition} /> : current.definition}
                </div>
              </ReactCardFlip>
            </div>

            <div className={classes.options}>
              <Button
                onClick={handleRepeat}
                className={wordOnScreen === currentWords.length - 1 ? `${classes.optionButton} ${classes.lastWord}` : classes.optionButton}
                disabled={isTransitioning}
                aria-label={t('games:labels.repeatWord')}
              >
                <Repeat className={classes.btnLabel} />
                <br />
                {t('games:buttons.repeat')}
              </Button>

              <Button
                onClick={handleNextClick}
                className={classes.optionButton}
                disabled={isTransitioning}
                aria-label={t('games:labels.nextWord')}
              >
                <Correct className={classes.btnLabel} />
                <br />
                {t('games:buttons.correct')}
              </Button>
            </div>
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} kiddo />
        )}

        {/* Podpis autora zdjęcia z Unsplasha — tylko gdy obrazek jest odkryty */}
        {!listEndInfo && <UnsplashCredits words={isFlipped && current ? [current] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && (
            <EditTermButton
              wordId={getListWordId(current)}
              currentTerm={current.term}
              currentDefinition={current.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(current);

                setCurrentWords((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}
        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(KiddoRecognizeView);
